import { Loader } from "@circle-react-uikit/Loader";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isEmpty } from "lodash";
import type { CSSProperties } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useMemberAreaScrollContainerId } from "@/react/hooks/useMemberAreaScrollContainerId";

import { useNotificationContext } from "../NotificationContext";
import { useFetchNotifications } from "../useHeaderNotificationsApi";
import { ListWrapper } from "./ListWrapper";
import { NoNotifications } from "./NoNotifications";
import { PageRecord } from "./PageRecord";
import { SkeletonLoader } from "./SkeletonLoader";

export interface NotificationsListProps {
  className?: string;
  isFullPage?: boolean;
  /**
   * Full-page only: id of the element infinite scroll listens to. Defaults
   * to the member-area scroll container; pass one when the list sits in its
   * own scroll region (e.g. the mobile Unified Inbox).
   */
  scrollableTargetId?: string;
  style?: CSSProperties;
}

export const NotificationsList = ({
  className,
  style,
  isFullPage = false,
  scrollableTargetId,
}: NotificationsListProps) => {
  const { activeFilter, activeTab, lastMarkedAsRead } =
    useNotificationContext();
  const scrollContainerId = useMemberAreaScrollContainerId();

  const perPage = isFullPage ? 20 : 15;
  const { isLoading, notifications, fetchNextPage, hasNextPage } =
    useFetchNotifications({
      notificationGroup: activeTab.toLocaleLowerCase(),
      filter: activeFilter,
      perPage: perPage,
    });

  const { currentCommunity } = usePunditUserContext();

  const current_plan_tier = currentCommunity?.current_plan_tier;

  const getScrollableTarget = () => {
    if (!isFullPage) return "user-notifications-panel-header";
    return scrollableTargetId ?? scrollContainerId;
  };

  if (isLoading)
    return (
      <ListWrapper className={className} style={style} isFullPage={isFullPage}>
        <SkeletonLoader rows={perPage} />
      </ListWrapper>
    );

  if (
    !notifications ||
    !notifications.pages[0] ||
    isEmpty(notifications.pages[0].records)
  ) {
    return <NoNotifications className={className} style={style} />;
  }

  return (
    <ListWrapper className={className} style={style} isFullPage={isFullPage}>
      <InfiniteScroll
        style={{
          overflow: "auto",
        }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={hasNextPage}
        dataLength={notifications.pages.length}
        scrollableTarget={getScrollableTarget()}
        loader={<Loader center />}
      >
        {notifications.pages.map(page =>
          page.records.map(notification => (
            <PageRecord
              key={notification.id}
              notification={notification}
              lastMarkedAsRead={lastMarkedAsRead}
            />
          )),
        )}
      </InfiniteScroll>
      {current_plan_tier && (
        <div className="w-full py-2 text-center">
          <Typography.LabelXs>
            {t("notification_retention_message", {
              months: t(`notification_retention_months.${current_plan_tier}`),
            })}
          </Typography.LabelXs>
        </div>
      )}
    </ListWrapper>
  );
};
